import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { INIT_GLOBALS } from "../../constants/ActionTypes";
import { getUserFriendlyErrorMessage } from "../../api/UserFriendlyError";
import { ReduxRootState } from "../../types/redux-store";
import { resolveReduxAppStore } from "../lib/redux-app-store";
import { reloadPage } from "../../utils/browser-navigation";

interface CurrentUser {
  plan: string;
  role: string;
  adminEmail?: string;
  pricingVersion?: string;
}

export type CurrentUserState = Partial<CurrentUser> & {
  currency?: string;
  subscription?: {
    subscription?: {
      plan_id?: string;
      status?: string;
      billing_period_unit?: string;
      current_term_end?: number;
    };
  };
  preferences?: {
    canAddAccounts?: boolean;
    canBoostPosts?: boolean;
    enableNewSettings?: boolean;
    entitlements?: Record<string, string | undefined>;
    timePreference?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

const emptyCurrentUser: CurrentUserState = {};

const DateTimeFormats = {
  "24h": "H:mm",
  "12h": "h:mm a",
} as const;

export function currentUserChanged(user: CurrentUserState) {
  const appStore = resolveReduxAppStore();

  if (!appStore) {
    return;
  }

  appStore.dispatch({
    type: INIT_GLOBALS,
    payload: {
      user,
    },
  });
}

export const selectCurrentUser = (state: ReduxRootState) =>
  state.user ?? emptyCurrentUser;

export function useCurrentUser(): CurrentUserState {
  return useSelector(selectCurrentUser);
}

/** Admins, and collaborators given the canAddAccounts preference. */
export function canUserAddAccounts(user: CurrentUserState): boolean {
  return user.role === "admin" || user.preferences?.canAddAccounts === true;
}

export function useCanAddAccounts(): boolean {
  return useSelector((state: ReduxRootState) =>
    canUserAddAccounts(selectCurrentUser(state))
  );
}

export function useUserCurrency(): string {
  const user = useCurrentUser();
  return user?.currency?.toUpperCase() ?? "USD";
}

export function subscriptionChanged() {
  try {
    reloadPage();
  } catch (error) {
    toast.error(getUserFriendlyErrorMessage(error as Error));
  }
}

export function useTimePreferences(): string {
  const user = useCurrentUser();
  return user?.preferences?.timePreference ?? "24h";
}

export function useUserTimeFormat(): string {
  const timePreferences = useTimePreferences();
  return (
    DateTimeFormats[timePreferences as keyof typeof DateTimeFormats] ?? "H:mm"
  );
}
